import { useCallback } from "react";

import { useBlizzardEvents } from "../../analytics/hooks";
import { useTags } from "../../lens-tags/hooks";
import { type ModalProps, useToggleState } from "../../shared/hooks";
import { lensTagsAtomById } from "../../shared/state";

import { useLensChangeTracker } from "./useLensChangeTracker";

export const useTagsForm = (
    tags: readonly string[],
    lensOrSubmissionId: string,
): {
    tagsList: readonly string[];
    addTag: (tag: string) => void;
    removeTag: (tag: string) => void;
    addTagsList: (tags: string[]) => void;
    setTagsLoading: boolean;
    openTagsModal: () => void;
    applyTags: () => void;
    tagsModalProps: ModalProps;
} => {
    const { sendBlizzardEvent } = useBlizzardEvents();
    const { tagsList, addTag, removeTag, addTagsList } = useTags(lensTagsAtomById(lensOrSubmissionId));
    const { updateTags, updateLensLoading } = useLensChangeTracker(lensOrSubmissionId);

    const [tagsModalProps, showTagsModal] = useToggleState();

    const openTagsModal = useCallback(() => {
        addTagsList(tags);

        showTagsModal();
    }, [tags, addTagsList, showTagsModal]);

    const applyTags = useCallback(() => {
        updateTags([...tagsList]);

        sendBlizzardEvent({
            event: "MYLENSES_MANAGEMENT_PAGE_ACTION",
            pageType: "LENS_DETAILS",
            action: "EDIT_TAGS",
        });
        tagsModalProps.onCancel();
    }, [updateTags, tagsList, tagsModalProps, sendBlizzardEvent]);

    return {
        tagsList,
        addTag,
        removeTag,
        addTagsList,
        setTagsLoading: updateLensLoading,
        openTagsModal,
        applyTags,
        tagsModalProps,
    };
};
